/*
  REUSABLE CONFIRMATION DIALOG COMPONENT
  Custom implementation using PrimeReact Dialog for full control
*/

import React, { useState, useCallback, createContext, useContext } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

// Create a context for the confirmation dialog
const ConfirmationContext = createContext(null);

export const useConfirmation = () => {
  return useContext(ConfirmationContext);
};

export const ShowConfirmationProvider = ({ children }) => {
  const [state, setState] = useState({
    visible: false,
    message: "",
    header: "",
    icon: "",
    acceptLabel: "Yes",
    rejectLabel: "No",
    acceptClassName: "",
    rejectClassName: "",
    accept: () => {},
    reject: () => {},
    showRejectButton: true,
  });

  const showConfirmation = useCallback(
    ({
      message = "Are you sure you want to proceed?",
      header = "Confirmation",
      icon = "pi pi-exclamation-triangle",
      accept,
      reject,
      acceptLabel = "Yes",
      rejectLabel,
      acceptClassName = "p-button-danger",
      rejectClassName = "p-button-secondary",
    }) => {
      setState({
        visible: true,
        message,
        header,
        icon,
        acceptLabel,
        rejectLabel: rejectLabel || "No",
        acceptClassName,
        rejectClassName,
        accept: accept || (() => {}),
        reject: reject || (() => {}),
        showRejectButton: !!rejectLabel,
      });
    },
    [],
  );

  const hideDialog = useCallback(() => {
    setState((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleAccept = () => {
    if (state.accept) {
      state.accept();
    }
    setState((prev) => ({ ...prev, visible: false }));
  };

  const handleReject = () => {
    if (state.reject) {
      state.reject();
    }
    setState((prev) => ({ ...prev, visible: false }));
  };

  return (
    <ConfirmationContext.Provider value={showConfirmation}>
      {children}
      <Dialog
        visible={state.visible}
        onHide={hideDialog}
        header={state.header}
        modal
        style={{ width: "450px" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        footer={
          <div className="flex gap-2 justify-end">
            {state.showRejectButton && (
              <Button
                label={state.rejectLabel}
                className={state.rejectClassName || "p-button-secondary"}
                onClick={handleReject}
                outlined
              />
            )}
            <Button
              label={state.acceptLabel}
              className={state.acceptClassName || "p-button-danger"}
              onClick={handleAccept}
              autoFocus
            />
          </div>
        }
      >
        <div className="flex items-start gap-3">
          {state.icon && <i className={`${state.icon} text-xl`} />}
          <p className="m-0">{state.message}</p>
        </div>
      </Dialog>
    </ConfirmationContext.Provider>
  );
};

// Legacy export for backward compatibility
export const showConfirmation = (options) => {
  console.warn("showConfirmation should be used through useConfirmation hook");
};

// Default export for backward compatibility with direct component usage
export default ShowConfirmationProvider;
